/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { useCallback, useEffect, useState } from "react";

//@Packages
import { debounce } from "lodash";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  AlertCircleIcon,
  Badge,
  Button,
  CircularLoader,
  EditIcon,
  EmptyState,
  LinkIcon,
  StatusBadge,
  Table,
  TableContent,
  TableHeader,
  TextOverflow,
  Tooltip,
  useTableSkeleton,
} from "@forge/common";

//@Components
import NoDevice from "../nodevice";
import ActionModal from "../ActionModal";
import DeleteModal from "../deletemodal";
import AssignSites from "../AssignSites";
import RevokeAuthModal from "../revokeauthmodal";
import CustomizedTable from "../../reusablecomponents/customizedtable";
import CustomizeColumns from "../../reusablecomponents/customizecolumns";
import { ContextualMenuComponent } from "../../reusablecomponents/contextualmenu";

//@Models
import FilterModal from "../FilterModal/FilterModal";
import {
  DeviceListHeaderProps,
  DeviceListProps,
  RequestObjectProps,
  Site,
  SortDirProps,
  SortObjectProps,
} from "../../models/devicelist";

//@Actions
import { setDeviceListUpdated } from "../../actions";

//@Store
import { RootState } from "../../store";

//@Services
import { useGetDeviceListQuery } from "../../services/api";

//@Static Data
import { getDeviceTableHeaders } from "../../utils/staticdata";

//@Utils
import { buildSiteListQuery } from "../../utils/siteContext";
import { notifyParent } from "../../utils/notifyParent";
import { useNavigateToLandingPage } from "../../utils/navigationUtils";
import { GatewayStatus, LOCALE_CONST, ROUTE_PATH } from "../../utils/constants";
import {
  convertUTCStringtoFormattedDate,
  validateInput,
  gatewayErrorStatusMessages,
} from "../../utils/utility";

//@Styles
import "./styles.scss";
import { useFlags } from "launchdarkly-react-client-sdk";

//@Functional component
interface DeviceListComponentProps {
  onLinkGateway?: (item: DeviceListProps) => void;
  linkingDeviceId?: string;
}

const DeviceList = ({ onLinkGateway, linkingDeviceId }: DeviceListComponentProps) => {
  const flags = useFlags();
  const DEBOUNCE_DELAY = 500;
  const TIMEOUT_DELAY = 1000;
  const MAX_SEARCH_LENGTH = 3;
  const TABLE_SKELETON_COUNT = 10;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const convertFn = (value: string) => t(value);

  const { navigateToLandingPage } = useNavigateToLandingPage();

  const {
    orgLists,
    selectedOrg,
    deviceListUpdated,
    customerOrgId,
    customerType,
    siteDeviceId,
    siteType,
    siteView,
    siteId,
    deviceListFilter,
    deviceListPagination,
  } = useSelector((state: RootState) => state.global);

  const isSiteList = siteView === "list";
  const isAssignPanel = siteView === "assignPanel";

  const deviceListHeaders = getDeviceTableHeaders(convertFn);

  const getFilterQuery = () => {
    if (isSiteList) {
      return buildSiteListQuery(
        customerOrgId,
        siteId,
        deviceListFilter.isFilterApplied ? deviceListFilter.filterReqObj : undefined,
      );
    }

    if (isAssignPanel) {
      return deviceListFilter.filterReqObj;
    }

    return customerType &&
      customerOrgId !== "" &&
      !deviceListFilter.isFilterApplied
      ? `servesto=${customerOrgId}`
      : deviceListFilter.filterReqObj;
  };

  // Helper function to get tag display text
  const getTagDisplayText = (tag: string) => {
    if (!tag) return null;

    // Don't display badge for forgeedge (both raw API value and formatted text)
    if (
      tag?.toLowerCase() === "forgeedge" ||
      tag?.toLowerCase() === "forge edge"
    )
      return null;

    // The API now provides the display text directly (e.g., "Forge Edge")
    // but we still handle legacy formats if needed
    switch (tag?.toLowerCase()) {
      case "legacy":
        return t(LOCALE_CONST.legacy);
      case "forgelegacy":
        return t(LOCALE_CONST.forgelegacy);
      case "azure":
        return t(LOCALE_CONST.fc); // "FC" as per requirement
      case "fc":
        return t(LOCALE_CONST.fc);
      default:
        return tag; // Return the tag as-is since API provides formatted text
    }
  };

  const [deviceData, setDeviceData] = useState<any>([]);
  const [deviceLists, setDeviceLists] = useState<DeviceListProps[]>([]);
  const [sortObj, setSortObj] = useState<SortObjectProps>({
    sortBy: "",
    sortDir: undefined,
  });
  const [requestObject, setRequestObject] = useState<RequestObjectProps>({
    limit: deviceListPagination.pageSize,
    pageNumber: deviceListPagination.page,
    filterQuery: getFilterQuery(),
  });
  const [isLoading, setIsLoading] = useState(true);

  const [errorData, setErrorData] = useState<any>([]);
  const [isSearch, setIsSearch] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [isCustomizeColumnsOpen, setIsCustomizeColumnsOpen] = useState(false);
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [isAssignSitesOpen, setIsAssignSitesOpen] = useState(false);
  const [tableHeaders, settableHeaders] =
    useState<DeviceListHeaderProps[]>(deviceListHeaders);
  const [filterHedaers, setFilterHeaders] =
    useState<DeviceListHeaderProps[]>(deviceListHeaders);
  const [actionType, setActionType] = useState<"Activate" | "Deactivate">(
    "Deactivate",
  );
  const [selectedActionItem, setSelectedActionItem] = useState<
    DeviceListProps | undefined
  >();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState<
    DeviceListProps | undefined
  >();
  const [selectedRevokeItem, setSelectedRevokeItem] = useState<
    DeviceListProps | undefined
  >();
  const {
    data: deviceListData,
    isError: deviceListError,
    error: deviceListErrorData,
    isSuccess: deviceListSuccess,
    isLoading: deviceListLoading,
    isFetching: deviceListFetching,
    refetch: refetchDeviceList,
  } = useGetDeviceListQuery(
    { ...requestObject, organizationId: selectedOrg, ...sortObj },
    { skip: !selectedOrg || (Boolean(siteView) && !customerOrgId) },
  );

  const getActionMenuItems = (
    item: DeviceListProps,
    status: string,
    isLegacyDevice: Boolean,
  ) => {
    if (isLegacyDevice) {
      return [
        {
          id: `regis-${item.id}`,
          label: t(LOCALE_CONST.reregister),
          onClick: () => handleRegistrationClick(item),
        },
      ];
    }
    if (item.connectivityType === "Disconnected") {
      return [
        {
          id: `del-${item.id}`,
          label: t(LOCALE_CONST.delete),
          onClick: () => handleDeleteClick(item),
        },
      ];
    } else if (status === GatewayStatus.Pending && item) {
      return [
        {
          id: `deact-${item.id}`,
          label: t(LOCALE_CONST.deactivate),
          onClick: () => handleActionClick("Deactivate", item),
        }
      ];
    } else if (
      status === GatewayStatus.Active ||
      status === GatewayStatus.Error
    ) {
      return [
        {
          id: `deact-${item.id}`,
          label: t(LOCALE_CONST.deactivate),
          onClick: () => handleActionClick("Deactivate", item),
        },
        {
          id: `revo-${item.id}`,
          label: t(LOCALE_CONST.revokeAuth),
          onClick: () => handleRevokeAuth("Revoke", item),
        }
      ];
    } else if (status === GatewayStatus.Deactivated) {
      return [
        {
          id: `act-${item.id}`,
          label: t(LOCALE_CONST.activate),
          onClick: () => handleActionClick("Activate", item),
        },
        {
          id: `del-${item.id}`,
          label: t(LOCALE_CONST.delete),
          onClick: () => handleDeleteClick(item),
        },
      ];
    } else {
      return [];
    }
  };

  // This is for the customer tab page only
  useEffect(() => {
    if (customerType && customerOrgId) {
      const updatedHeaders = deviceListHeaders.filter(
        (header) => header.id !== "organizationName",
      );
      settableHeaders(updatedHeaders);
    }

    // This is for the customer tab page only for the device id
    if (siteType && siteDeviceId !== "" && !siteView) {
      navigate(`${ROUTE_PATH.viewDevice}/${siteDeviceId}`);
    }
  }, [customerType, customerOrgId, siteDeviceId, siteType]);
  // End of customer tab page only

  useEffect(() => {
    if (deviceListUpdated) {
      dispatch(setDeviceListUpdated({ deviceListUpdated: false }));
      refetchDeviceList();
    }
  }, [deviceListUpdated]);

  useEffect(() => {
    if (deviceListSuccess && deviceListData !== undefined) {
      setIsLoading(false);
      setDeviceData(deviceListData);
      setDeviceLists(deviceListData?.data || []);
    } else if (deviceListError) {
      setIsLoading(false);
      setErrorData(deviceListErrorData);
      setDeviceData([]);
      setDeviceLists([]);
    }
  }, [deviceListData, deviceListError, deviceListSuccess]);

  useEffect(() => {
    const getFilterData = tableHeaders.filter(
      (item: DeviceListHeaderProps) => item.ischecked,
    );
    setFilterHeaders(getFilterData);
  }, [tableHeaders]);

  useEffect(() => {
    const sortedData = tableHeaders.map((item: DeviceListHeaderProps) => {
      if (sortObj.sortBy === item.id && item.canSort) {
        return { ...item, sort: sortObj.sortDir };
      } else {
        return item;
      }
    });

    setFilterHeaders(sortedData);
  }, [sortObj]);

  // Sync requestObject with pagination and filter changes from Redux store
  useEffect(() => {
    setRequestObject((prevValue) => ({
      ...prevValue,
      limit: deviceListPagination.pageSize,
      pageNumber: deviceListPagination.page,
      filterQuery: getFilterQuery(),
    }));
  }, [
    deviceListPagination.pageSize,
    deviceListPagination.page,
    deviceListFilter.filterReqObj,
    deviceListFilter.isFilterApplied,
    customerType,
    customerOrgId,
    siteView,
    siteId,
  ]);

  const formatContentData = (content: DeviceListProps[]) => {
    const getColorClsName = (status: string = "") => {
      if (status === GatewayStatus.Active) return "green";
      if (status === GatewayStatus.Pending) return "blue";
      if (
        status === GatewayStatus.Error ||
        status === GatewayStatus.CertificateExpired
      )
        return "red";
      return "gray";
    };

    const formatCellContent = (value: any) => {
      if (Array.isArray(value)) {
        return value.map((site) => site.name).join(", ") || "-";
      }
      return value || "-";
    };

    const formatConnectivityTypeCellContent = (value: any) => {
      return value || "-";
    };

    const isActionMenuDisabled = (status: string) => {
      if (
        status === GatewayStatus.Activating ||
        status === GatewayStatus.Deactivating ||
        status === GatewayStatus.Deleting ||
        status === GatewayStatus.CertificateExpired
      ) {
        return true;
      } else {
        return false;
      }
    };

    const displayStatus = (status: string, lastHeartBeatTimeStamp?: string) => {
      if (status === GatewayStatus.Active) {
        return t(LOCALE_CONST.active);
      } else if (status === GatewayStatus.Error) {
        const errorDescription = (val: string) => {
          let error = t(LOCALE_CONST.gwConnectionFailed);

          if (val) {
            error = `${t(LOCALE_CONST.gwConnectionFailedAt)} ${convertUTCStringtoFormattedDate(val)}`;
          }

          return error;
        };

        return (
          <span className="iot-error-status-cls">
            {t(LOCALE_CONST.error)}
            <Tooltip content={errorDescription(lastHeartBeatTimeStamp)}>
              <AlertCircleIcon />
            </Tooltip>
          </span>
        );
      } else if (status === GatewayStatus.Pending) {
        return t(LOCALE_CONST.pending);
      } else if (status === GatewayStatus.Activating) {
        return t(LOCALE_CONST.activating);
      } else if (status === GatewayStatus.Deactivating) {
        return t(LOCALE_CONST.deactivating);
      } else if (status === GatewayStatus.Deleting) {
        return t(LOCALE_CONST.deleting);
      } else if (status === GatewayStatus.CertificateExpired) {
        return t(LOCALE_CONST.certificateExpired);
      } else {
        return t(LOCALE_CONST.deactivated);
      }
    };

    const getOrgName = (orgId: string) =>
      orgLists?.find((item) => item.value === orgId)?.text || "-";

    const ellipsisTextComp = (textVal: string) => {
      return (
        <TextOverflow resizeDebounceDelay={300} tooltipPosition="top">
          {textVal}
        </TextOverflow>
      );
    };

    return content.map((item: any) => {
      const isLegacyDevice =
        item.providerName === "legacy" || item.providerName === "forgelegacy";
      const isLegacyFlagEnabled =
        flags.isLegacyGateway?.actionItemsDisabled === "True";
      return {
        id: item.id,
        cell: filterHedaers.map((header) => {
          let cellContent: any = (
            <span>{formatCellContent(item[header.id])}</span>
          );

          if (
            header.id === "name" &&
            (item.status === GatewayStatus.Pending || siteView)
          ) {
            cellContent = ellipsisTextComp(item.name);
          }

          if (
            header.id === "name" &&
            item.status !== GatewayStatus.Pending &&
            !siteView
          ) {
            cellContent = (
              <div
                className="device-name-link"
                data-testid="device-name-link-test"
                onClick={() => {
                  sessionStorage.setItem("systemGuid", item?.deviceId);
                  sessionStorage.setItem(
                    "linkedDevicesSystemGuid",
                    item?.deviceId,
                  );
                  navigate(`${ROUTE_PATH.viewDevice}/${item?.deviceId}`);
                }}
              >
                {ellipsisTextComp(item.name)}
              </div>
            );
          }

          if (header.id === "status") {
            cellContent =
              item.status === GatewayStatus.Deactivating ||
              item.status === GatewayStatus.Activating ? (
                <span className="flex items-center">
                  <CircularLoader
                    variant="primary"
                    size="small"
                    showTrack={false}
                    className="mr-1"
                  />
                  {displayStatus(item.status)}
                </span>
              ) : (
                <span
                  className={`${
                    item.status === GatewayStatus.Error
                      ? "flex items-center"
                      : ""
                  }`}
                >
                  <StatusBadge
                    className={`mr-1 text-[0.5rem]`}
                    color={getColorClsName(item.status)}
                  />
                  {displayStatus(item.status, item.lastHeartBeatTimeStamp)}
                </span>
              );
          }

          if (header.id === "gatewayType") {
            const tagDisplayText = getTagDisplayText(item.tag || "");
            cellContent = (
              <span>
                {formatCellContent(item[header.id])}
                {tagDisplayText && (
                  <Badge className="ml-1" color="gray">
                    {tagDisplayText}
                  </Badge>
                )}
              </span>
            );
          }

          if (header.id === "connectivityType") {
            cellContent = (
              <span>{formatConnectivityTypeCellContent(item[header.id])}</span>
            );
          }

          if (header.id === "organizationId") {
            cellContent = ellipsisTextComp(getOrgName(item.organizationId));
          }

          if (header.id === "site") {
            const siteNames =
              (item.site && item.site.map((site: Site) => site.name)) || [];
            cellContent = (
              <>
                {item.site && item.site.length > 0 ? (
                  <Tooltip content={siteNames.join(", ")} position="top">
                    <span className="guid-col">
                      {ellipsisTextComp(siteNames.join(", ") || "-")}
                    </span>
                  </Tooltip>
                ) : (
                  "-"
                )}
              </>
            );
          }
          if (
            header.id === "actions" &&
            item.status === GatewayStatus.Pending
          ) {
            cellContent = (
              <div className="gateway-action-button-container">
                <Button
                  aria-label="Settings"
                  data-testid="edit-device"
                  className="disabled-edit-icon-cls"
                  icon={<EditIcon strokeWidth={2.5} color="gray" />}
                  variant="quiet-secondary"
                />
                <ContextualMenuComponent
                  provider={item.providerName}
                  menuItems={
                    getActionMenuItems(item, item.status, isLegacyDevice) || []
                  }
                  disabled={isActionMenuDisabled(item.status)}
                />
              </div>
            );
          }
          if (
            header.id === "actions" &&
            item.status !== GatewayStatus.Pending
          ) {
            const validEditStatus =
              item.status === GatewayStatus.Deactivated ||
              item.status === GatewayStatus.Active ||
              item.status === GatewayStatus.Error;
            cellContent = (
              <div className="gateway-action-button-container">
                <Button
                  aria-label="Settings"
                  data-testid="edit-device"
                  icon={
                    <EditIcon
                      strokeWidth={2.5}
                      color={`${!validEditStatus ? "" : "var(--link-name-color)"}`}
                    />
                  }
                  variant="quiet-secondary"
                  disabled={!validEditStatus}
                  onClick={() =>
                    navigate(`${ROUTE_PATH.editDevice}/${item.deviceId}`)
                  }
                />
                <ContextualMenuComponent
                  provider={item.providerName}
                  menuItems={
                    getActionMenuItems(item, item.status, isLegacyDevice) || []
                  }
                  disabled={isActionMenuDisabled(item.status)}
                />
              </div>
            );
          }
          if (
            header.id === "actions" &&
            (item.status === GatewayStatus.CertificateExpired ||
              item.status === GatewayStatus.Pending)
          ) {
            cellContent = (
              <div className="gateway-action-button-container">
                <Button
                  aria-label="Settings"
                  data-testid="edit-device"
                  icon={
                    <EditIcon
                      strokeWidth={2.5}
                      color="var(--link-name-color)"
                    />
                  }
                  variant="quiet-secondary"
                  onClick={() =>
                    navigate(`${ROUTE_PATH.reRegistration}/${item.deviceId}`)
                  }
                />
                <ContextualMenuComponent
                  provider={item.providerName}
                  menuItems={
                    getActionMenuItems(item, item.status, isLegacyDevice) || []
                  }
                  disabled={isActionMenuDisabled(item.status)}
                />
              </div>
            );
          }

          if (header.id === "actions" && isSiteList) {
            // Site context: no edit/view icon, lifecycle menu only.
            cellContent = (
              <div className="gateway-action-button-container">
                <ContextualMenuComponent
                  provider={item.providerName}
                  menuItems={
                    getActionMenuItems(item, item.status, isLegacyDevice) || []
                  }
                  disabled={isActionMenuDisabled(item.status)}
                />
              </div>
            );
          }

          if (header.id === "actions" && isAssignPanel) {
            const linkedToOtherCustomer =
              Boolean(item.organizationId) && item.organizationId !== customerOrgId;
            const linkedToThisSite =
              Array.isArray(item.site) &&
              item.site.some((site: Site) => site?.id === siteId);
            cellContent = (
              <Button
                variant="secondary"
                icon={<LinkIcon strokeWidth={2} />}
                iconPosition="left"
                data-testid="link-gateway"
                disabled={
                  item.status === GatewayStatus.Pending ||
                  linkedToOtherCustomer ||
                  linkedToThisSite ||
                  Boolean(linkingDeviceId)
                }
                onClick={() => onLinkGateway?.(item)}
              >
                {linkedToThisSite ? t(LOCALE_CONST.linked) : t(LOCALE_CONST.link)}
              </Button>
            );
          }

          if (header.id === "registrationTime") {
            cellContent = (
              <span className="gateway-registration-time">
                {convertUTCStringtoFormattedDate(item.registrationTime) || "-"}
              </span>
            );
          }

          return { content: cellContent };
        }),
      };
    });
  };

  const handleActionClick = (
    type: "Activate" | "Deactivate",
    item: DeviceListProps,
  ) => {
    setActionType(type);
    setSelectedActionItem(item);
    setIsActionModalOpen(true);
  };

  const handleRevokeAuth = (type: "Revoke", item: DeviceListProps) => {
    setSelectedRevokeItem(item);
    setIsRevokeModalOpen(true);
  };

  const handleRegistrationClick = (item: DeviceListProps) => {
    navigate(`${ROUTE_PATH.reRegistration}/${item.deviceId}`);
  };

  const handleDeleteClick = (item: DeviceListProps) => {
    setSelectedDeleteItem(item);
    setIsDeleteModalOpen(true);
  };

  const showHideIconClick = () => setIsCustomizeColumnsOpen(true);
  const filterIconClick = () =>
    dispatch({
      type: "SET_FILTER_ITEMS",
      payload: {
        deviceListFilter: {
          ...deviceListFilter,
          isFilterOpen: true,
        },
      },
    });

  const assignSitesClick = () => navigate(ROUTE_PATH.bulkAssignment);
  const closeAssignSites = () => setIsAssignSitesOpen(false);
  const handleAssignSitesConfirm = () => {
    setIsAssignSitesOpen(false);
  };

  const tableSkeleton = useTableSkeleton(
    TABLE_SKELETON_COUNT,
    filterHedaers.length,
  );

  const onPageChange = (page: number) => {
    dispatch({
      type: "SET_DEVICE_LIST_PAGINATION",
      payload: {
        deviceListPagination: {
          page: page,
          pageSize: deviceData.limit,
        },
      },
    });
  };

  const onPageSizeChange = (pageSize: number) => {
    if (
      requestObject.pageNumber >= Math.ceil(deviceData.totalRecords / pageSize)
    ) {
      dispatch({
        type: "SET_DEVICE_LIST_PAGINATION",
        payload: {
          deviceListPagination: {
            page: 1,
            pageSize: pageSize,
          },
        },
      });
    } else {
      dispatch({
        type: "SET_DEVICE_LIST_PAGINATION",
        payload: {
          deviceListPagination: {
            page: requestObject.pageNumber,
            pageSize: pageSize,
          },
        },
      });
    }
  };

  // For Search
  const normalizeSpaces = (str: string) =>
    str.toLowerCase().replace(/\s+/g, " ").trim();
  const searchOnChange = (value: string) => {
    if (value === "") {
      setTimeout(() => {
        setIsSearch(false);
      }, TIMEOUT_DELAY);
      debouncedSearchTextFetch(normalizeSpaces(value));
    } else {
      setIsSearch(true);
      if (value.split(" ").join("").length >= MAX_SEARCH_LENGTH)
        debouncedSearchTextFetch(normalizeSpaces(value));
    }
    if (validateInput(value)) {
      setSearchValue(value);
    }
  };

  const debouncedSearchTextFetch = useCallback(
    debounce((text: string) => {
      setRequestObject((prevValue) => {
        return {
          ...prevValue,
          pageNumber: 1,
          filter: text,
        };
      });
    }, DEBOUNCE_DELAY),
    [],
  );

  const onSearchClear = () => {
    setTimeout(() => {
      setIsSearch(false);
    }, TIMEOUT_DELAY);
    setSearchValue("");
    debouncedSearchTextFetch("");
  };

  const onListSortUpdate = (dir: SortDirProps, id: string) =>
    setSortObj({ sortBy: id, sortDir: dir });

  const pageReload = () => navigateToLandingPage();
  const handleNavigateToGatewayManagement = () => {
    notifyParent({ gwRedirectTrigger: true });
  };

  // Site list only: the parent keeps the Gateway tab count in step with the list.
  const handleGatewayDeleted = () => {
    notifyParent({ gatewayDeleted: true, siteId });
  };

  const renderDeviceList = () => {
    const statusCode = 404;
    const statusVal = errorData?.originalStatus || errorData?.status;

    if (deviceListLoading || deviceListFetching || isLoading) {
      return (
        <Table>
          <TableHeader data={filterHedaers} />
          <TableContent data={tableSkeleton} />
        </Table>
      );
    } else if (deviceListError && statusVal !== statusCode) {
      return (
        <EmptyState
          variant="4xx"
          title={gatewayErrorStatusMessages(
            convertFn,
            `${t(LOCALE_CONST.somethingWentWrong)}...`,
            statusVal,
            {
              401: t(LOCALE_CONST.clientError_401),
            },
          )}
          description={`${t(LOCALE_CONST.tryReloading)}. (${t(LOCALE_CONST.errCode, { errorCode: statusVal || t(LOCALE_CONST.error) })})`}
        >
          <Button className="mx-auto block" onClick={pageReload}>
            {t(LOCALE_CONST.reloadPage)}
          </Button>
        </EmptyState>
      );
    } else if (
      deviceLists.length <= 0 &&
      !isSearch &&
      !deviceListFilter.isFilterApplied
    ) {
      if (siteView) {
        return (
          <NoDevice
            titleKey={t(LOCALE_CONST.gatewayManagement)}
            h2Text={t(LOCALE_CONST.noGatewayAvailable)}
            h3Text=""
          />
        );
      }

      if (customerType && customerOrgId !== "") {
        return (
          <NoDevice
            titleKey={t(LOCALE_CONST.gatewayManagement)}
            h2Text={t(LOCALE_CONST.noGatewayAvailable)}
            h3Text={t(LOCALE_CONST.assocGateway)}
            linkText={t(LOCALE_CONST.gatewayManagement)}
            buttonOnClick={handleNavigateToGatewayManagement}
          />
        );
      } else {
        return (
          <NoDevice
            titleKey={t(LOCALE_CONST.gatewayManagement)}
            h2Text={t(LOCALE_CONST.noGatewayCreated)}
            h3Text=""
          />
        );
      }
    } else {
      return (
        <CustomizedTable
          maxPages={7}
          canResize={false}
          isSearch={isSearch}
          tableData={deviceLists}
          searchValue={searchValue}
          pageSize={deviceData.limit}
          onPageChange={onPageChange}
          onSearchClear={onSearchClear}
          onUpdateSort={onListSortUpdate}
          tableHeaderData={filterHedaers}
          searchOnChange={searchOnChange}
          filterIconClick={filterIconClick}
          assignSitesClick={assignSitesClick}
          onPageSizeChange={onPageSizeChange}
          initialPage={deviceData.currentPage}
          totalItems={deviceData.totalRecords}
          showHideIconClick={showHideIconClick}
          title={t(LOCALE_CONST.gatewayOverview)}
          tableContentData={formatContentData(deviceLists)}
          totalPages={Math.ceil(deviceData.totalRecords / deviceData.limit)}
        />
      );
    }
  };

  return (
    <>
      {renderDeviceList()}
      <ActionModal
        actionType={actionType}
        isOpen={isActionModalOpen}
        onClose={() => setIsActionModalOpen(false)}
        deviceName={selectedActionItem?.name || ""}
        deviceId={selectedActionItem?.deviceId || ""}
        refetchDeviceList={refetchDeviceList}
      />
      <DeleteModal
        isOpen={isDeleteModalOpen}
        refetchDeviceList={refetchDeviceList}
        onDeleted={isSiteList ? handleGatewayDeleted : undefined}
        onClose={() => setIsDeleteModalOpen(false)}
        deviceName={selectedDeleteItem?.name || ""}
        deviceId={selectedDeleteItem?.deviceId || ""}
      />
      <RevokeAuthModal
        isOpen={isRevokeModalOpen}
        deviceId={selectedRevokeItem?.deviceId || ""}
        onClose={() => setIsRevokeModalOpen(false)}
        deviceName={selectedRevokeItem?.name || ""}
        refetchDeviceList={refetchDeviceList}
      />
      {isCustomizeColumnsOpen && (
        <CustomizeColumns
          tableHeaders={tableHeaders}
          settableHeaders={settableHeaders}
          setClose={setIsCustomizeColumnsOpen}
          initialTableHeaders={deviceListHeaders}
        />
      )}
      {deviceListFilter.isFilterOpen && <FilterModal />}
      {isAssignSitesOpen && (
        <AssignSites
          showAssignSite={isAssignSitesOpen}
          onClose={closeAssignSites}
          onConfirm={handleAssignSitesConfirm}
        />
      )}
    </>
  );
};

export default DeviceList;
